import { TextOverlayData, VectorOverlayData } from './sceneDesc.types';
import type { GuideTimedTextSummary, PublishedTimedText } from './timedText.types';

// Duration type - all durations in milliseconds
export type Duration = number;

export const DurationUtils = {
  zero: 0,
  fromSeconds: (s: number): Duration => s * 1000,
  fromMilliseconds: (ms: number): Duration => ms,
  toSeconds: (d: Duration): number => d / 1000,
  formatTime: (d: Duration): string => {
    const totalSeconds = Math.floor(d / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  },
};

// Runtime playback models
export interface PlayerItem {
  gboxId: string;
  gboxTitle: string;
  thumbnailPath?: string;
  clips: ClipItem[];
  chapters: ChapterInfo[];
  totalDuration: Duration;
  isOffline: boolean; // always false for web streaming
  timedText?: GuideTimedTextSummary;
}

export interface ChapterInfo {
  chapterId: string;
  title: string;
  isDefault: boolean; // chapter "0" is default
  clipIndices: number[]; // indices into PlayerItem.clips array
  clips: { id: string; name: string }[];
}

export interface ClipItem {
  clipId: string;
  clipName: string;
  clipDescription?: string;
  chapterId?: string;
  scenes: SceneItem[];
  audioSegments: AudioSegmentItem[];
  timedText?: PublishedTimedText;
  sceneDuration: Duration; // Sum of all scene durations
  audioDuration: Duration; // Max audio segment endTime (0 if no audio)
  totalDuration: Duration; // Max of sceneDuration and audioDuration
  latitude?: number;
  longitude?: number;
}

export interface SceneItem {
  sceneId: string;
  sceneName?: string;
  sceneDescription?: string;
  mediaPath: string; // URL for web player
  thumbnailPath?: string;
  mediaType: 'image' | 'video';
  duration: Duration;
  startTime: Duration; // start time within clip
  endTime: Duration; // end time within clip
  videoVolume?: number; // 0.0 - 2.0 for video scenes
  textOverlays?: TextOverlayData[]; // text overlays from sceneDesc
  vectorOverlays?: VectorOverlayData[]; // vector annotations (arrows/shapes/callouts) from sceneDesc
}

export interface AudioSegmentItem {
  id: string;
  audioSegmentId: string;
  mediaId?: string;
  filePath: string; // URL for web player
  mediaType?: string; // MIME type hint for browser (e.g., "audio/mp4")
  duration: Duration;
  startTime: Duration; // start time within clip
  endTime: Duration;
  trimStart?: Duration; // where to start reading from source file
  trimEnd?: Duration; // where to stop reading from source file
  volume: number; // 0.0 - 1.0
  label?: string;
  order?: number;
  spokenLanguageTag?: string | null;
  captionsEnabledByDefault: boolean;
}

// Playback state
export enum PlaybackStatus {
  Idle = 'idle',
  Loading = 'loading',
  Playing = 'playing',
  Paused = 'paused',
  Completed = 'completed',
  Error = 'error',
}

export interface PlaybackState {
  // Status
  status: PlaybackStatus;

  // Content
  currentItem: PlayerItem | null;

  // Position tracking
  currentClipIndex: number;
  currentSceneIndex: number; // Scene based on audio position
  displayedSceneIndex: number; // Scene currently shown (may differ if user swiped)
  isDesynchronized: boolean; // displayedScene != currentScene
  position: Duration; // Current position in clip (ms)
  clipDuration: Duration;

  // Audio controls
  volume: number; // 0.0 - 1.0
  isMuted: boolean;
  speed: number; // 0.5 - 2.0

  // UI state
  isMinimized: boolean;
  showControls: boolean;
  isBuffering: boolean;
  isFullscreen: boolean;

  // Error
  errorMessage?: string;
  audioError?: string; // Clip-specific audio error (doesn't block navigation)
}

// Player actions
export type PlayerAction =
  | { type: 'LOAD_GBOX'; gboxId: string; autoPlay?: boolean }
  | { type: 'CONTENT_LOADED'; playerItem: PlayerItem }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TOGGLE_PLAY_PAUSE' }
  | { type: 'SEEK_TO'; position: Duration }
  | { type: 'NEXT_SCENE' }
  | { type: 'PREVIOUS_SCENE' }
  | { type: 'JUMP_TO_SCENE'; sceneIndex: number }
  | { type: 'NEXT_CLIP' }
  | { type: 'PREVIOUS_CLIP' }
  | { type: 'JUMP_TO_CLIP'; clipIndex: number; sceneIndex?: number }
  | { type: 'SET_VOLUME'; volume: number }
  | { type: 'TOGGLE_MUTE' }
  | { type: 'SET_SPEED'; speed: number }
  | { type: 'MINIMIZE' }
  | { type: 'MAXIMIZE' }
  | { type: 'TOGGLE_CONTROLS' }
  | { type: 'SET_CONTROLS_VISIBLE'; visible: boolean }
  | { type: 'TOGGLE_FULLSCREEN' }
  | { type: 'SET_FULLSCREEN'; isFullscreen: boolean }
  | { type: 'STOP' }
  | { type: 'POSITION_UPDATED'; position: Duration }
  | { type: 'CLIP_COMPLETED' }
  | { type: 'AUDIO_ENDED' }
  | { type: 'SCENE_DISPLAY_CHANGED'; sceneIndex: number }
  | { type: 'SET_BUFFERING'; isBuffering: boolean }
  | { type: 'ERROR'; message: string }
  | { type: 'AUDIO_ERROR'; message: string }
  | { type: 'CLEAR_AUDIO_ERROR' }
  | { type: 'RESET' };

// Initial state
export const initialPlaybackState: PlaybackState = {
  status: PlaybackStatus.Idle,
  currentItem: null,
  currentClipIndex: 0,
  currentSceneIndex: 0,
  displayedSceneIndex: 0,
  isDesynchronized: false,
  position: 0,
  clipDuration: 0,
  volume: 1.0,
  isMuted: false,
  speed: 1.0,
  isMinimized: false,
  showControls: true,
  isBuffering: false,
  isFullscreen: false,
};
